import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Typography,
} from '@mui/material'
import { useState } from 'react'

import { useAppContext } from '@/hooks'
import { MassExportExternalConfigDto, useGetObjectSettingsQuery } from '@/rtkApi/mddApiSliceGeneratedV2'

import { groupObjectSettings, structureObjectPairs } from './objectSettingsSelection'

type GenerateScriptsConfigDialogProps = {
	deploymentPackageId: number
	envName?: string
	onClose: () => void
	onGenerate: (config: MassExportExternalConfigDto) => void
	structureId?: number
}

export const GenerateScriptsConfigDialog = ({
	deploymentPackageId,
	envName,
	onClose,
	onGenerate,
	structureId,
}: GenerateScriptsConfigDialogProps) => {
	const { t } = useAppContext()
	const { data, isLoading } = useGetObjectSettingsQuery({ dpId: deploymentPackageId })
	const [selectedIds, setSelectedIds] = useState<number[] | null>(null)
	const objects = data ?? []
	const groups = groupObjectSettings(objects)
	const selected = selectedIds ?? objects.flatMap((item) => (item.id == null ? [] : [item.id]))

	const toggle = (id: number) => {
		setSelectedIds(
			selected.includes(id) ? selected.filter((current) => current !== id) : [...selected, id],
		)
	}

	const toggleGroup = (ids: number[]) => {
		const allSelected = ids.every((id) => selected.includes(id))
		setSelectedIds(
			allSelected
				? selected.filter((id) => !ids.includes(id))
				: [...new Set([...selected, ...ids])],
		)
	}

	const generate = () => {
		onGenerate({
			ignoreErrors: false,
			massExportSorting: 'DEFAULT',
			massExportStrategy: 'SINGLE_FILE',
			structureIds: structureId == null ? [] : [structureId],
			structureObjectPairs: structureObjectPairs(objects, selected),
		})
	}

	return (
		<Dialog
			open
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			data-testid="deployment-package-generate-config"
			slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
		>
			<DialogTitle sx={{ color: '#1F4954', fontSize: 16, fontWeight: 700 }}>
				{t('DEPLOYMENT_PACKAGES_CREATE_SCRIPTS')}
				{envName ? `: ${envName}` : ''}
			</DialogTitle>
			<DialogContent>
				<Typography sx={{ color: '#6B6B6B', fontSize: 12, mb: 1.5 }}>
					{t('OBJECT_SETTINGS')}
				</Typography>
				{isLoading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 360, overflow: 'auto' }}>
						{groups.map((group) => {
							const ids = group.objects.flatMap((item) => (item.id == null ? [] : [item.id]))
							return (
								<Box key={group.structureType}>
									<Typography
										onClick={() => toggleGroup(ids)}
										sx={{
											color: '#1F4954',
											cursor: 'pointer',
											fontSize: 12,
											fontWeight: 700,
											letterSpacing: '0.04em',
										}}
									>
										{group.structureType}
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column', pl: 0.5 }}>
										{group.objects.map((item) =>
											item.id == null ? null : (
												<FormControlLabel
													key={item.id}
													control={
														<Checkbox
															size="small"
															checked={selected.includes(item.id)}
															onChange={() => toggle(item.id as number)}
														/>
													}
													label={item.code}
													sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
												/>
											),
										)}
									</Box>
								</Box>
							)
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ pb: 2, px: 3 }}>
				<Button
					variant="contained"
					disableElevation
					disabled={isLoading || selected.length === 0}
					onClick={generate}
					data-testid="deployment-package-generate-start"
					sx={{ textTransform: 'none' }}
				>
					{t('MASS_DOWNLOAD_BUTTON')}
				</Button>
				<Button onClick={onClose} sx={{ textTransform: 'none' }}>
					{t('CANCEL')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
